import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ScrollShadow,
} from '@heroui/react'
import { useMemo, useState } from 'react'
import ReactDOM from 'react-dom/client'

export type FolderInfo = {
  name: string
  files: File[]
  fileCount: number
  totalSize: number
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`
}

function MultiFolderUploadModal({
  open,
  folders,
  onAccept,
  onCancel,
}: {
  open: boolean
  folders: FolderInfo[]
  onAccept: () => void
  onCancel: () => void
}) {
  const [submitting, setSubmitting] = useState(false)

  const totals = useMemo(() => {
    const totalFiles = folders.reduce((s, f) => s + (f.fileCount ?? f.files.length), 0)
    const totalSize = folders.reduce((s, f) => s + (f.totalSize ?? 0), 0)

    return { totalFiles, totalSize }
  }, [folders])

  return (
    <Modal backdrop="blur" isOpen={open} size="lg" onOpenChange={onCancel}>
      <ModalContent>
        <>
          <ModalHeader className="flex flex-col items-start gap-1">
            Upload {folders.length} folder{folders.length > 1 ? 's' : ''}?
            <div className="text-sm font-normal text-default-500">
              Total: {totals.totalFiles} files ({formatFileSize(totals.totalSize)})
            </div>
          </ModalHeader>
          <ModalBody>
            <ScrollShadow className="max-h-[320px] pr-2">
              <ul className="space-y-2">
                {folders.map(f => (
                  <li
                    key={f.name}
                    className="flex items-center justify-between rounded-xl border border-default-200 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{f.name}</div>
                      <div className="text-xs text-default-500">
                        {f.fileCount} files • {formatFileSize(f.totalSize)}
                      </div>
                    </div>
                    <Chip radius="sm" size="sm" variant="flat">
                      Folder
                    </Chip>
                  </li>
                ))}
              </ul>
            </ScrollShadow>
          </ModalBody>
          <ModalFooter>
            <Button isDisabled={submitting} variant="flat" onPress={onCancel}>
              Cancel
            </Button>
            <Button
              color="primary"
              isLoading={submitting}
              onPress={() => {
                setSubmitting(true)
                onAccept()
              }}
            >
              Start Upload
            </Button>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  )
}

/** Imperative, promise-based API. Falls back to window.confirm if DOM/React Root fail. */
export function showMultiFolderModal(folders: FolderInfo[]): Promise<boolean> {
  try {
    if (typeof document === 'undefined') {
      return Promise.resolve(typeof window !== 'undefined' ? window.confirm('Proceed?') : false)
    }

    const host = document.createElement('div')

    document.body.appendChild(host)
    const root = ReactDOM.createRoot(host)

    return new Promise<boolean>(resolve => {
      const cleanup = () => {
        root.unmount()
        host.remove()
      }
      const handleAccept = () => {
        cleanup()
        resolve(true)
      }
      const handleCancel = () => {
        cleanup()
        resolve(false)
      }

      root.render(
        <MultiFolderUploadModal
          open
          folders={folders}
          onAccept={handleAccept}
          onCancel={handleCancel}
        />,
      )
    })
  } catch {
    const msg =
      `Upload ${folders.length} folders?\n\n` +
      folders
        .map(f => `• ${f.name}: ${f.fileCount} files (${formatFileSize(f.totalSize)})`)
        .join('\n')

    return Promise.resolve(window.confirm(msg))
  }
}
